import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import Select from 'react-select'
import { useReactToPrint } from 'react-to-print'

import { httpOk, GET, POST, PUT, DELETE, FILE_D } from 'helpers/api'
import { formatDate, formatMoney, returnSign } from 'helpers/helpers'
import HintIcon from 'pages/parts/HintIcon'
import PeriodPicker from 'pages/parts/PeriodPicker'

const SCHEDULE_PRINT_PAGE_STYLE = `
	@page {
		size: A4 landscape;
		margin: 8mm;
	}
	@media print {
		body {
			margin: 0;
			font-family: Arial, Helvetica, sans-serif;
			color: #000;
			-webkit-print-color-adjust: exact;
			print-color-adjust: exact;
		}
		.schedule-print,
		.schedule-print * {
			color: #000 !important;
			overflow: visible !important;
			max-height: none !important;
		}
		.schedule-print table {
			width: 100% !important;
			font-size: 10px !important;
		}
		.d-print-none {
			display: none !important;
		}
	}
`

/** Понедельник недели, в которую попадает дата */
function startOfWeek(date) {
	const copy = new Date(date)
	const weekday = (copy.getDay() + 6) % 7
	copy.setDate(copy.getDate() - weekday)
	return copy
}

function addDays(date, days) {
	const copy = new Date(date)
	copy.setDate(copy.getDate() + days)
	return copy
}

function Index() {
	const { t } = useTranslation()
	const printRef = useRef()

	const [period, setPeriod] = useState({
		start_date: formatDate(startOfWeek(new Date()), 'yyyy-MM-dd'),
		end_date: formatDate(addDays(startOfWeek(new Date()), 6), 'yyyy-MM-dd'),
	})
	const [data, setData] = useState({ dates: [], employees: [], shifts: [], shift_types: [] })
	/** «кисть»: выбранный тип смены ставится кликом по пустой ячейке */
	const [activeShiftTypeId, setActiveShiftTypeId] = useState(0)
	const [selectedShift, setSelectedShift] = useState(null)
	const [showCellModal, setShowCellModal] = useState(false)
	const [swapUserId, setSwapUserId] = useState(0)
	const [showCopyModal, setShowCopyModal] = useState(false)
	const [copyData, setCopyData] = useState({ target_start_date: '' })

	/** смены по ключу «сотрудник + дата» — сетка рисуется по этой карте */
	const shiftsByCell = useMemo(() => {
		const map = {}
		for (const shift of (data.shifts || [])) {
			const key = `${shift.user_id}_${shift.work_date}`
			if (!map[key]) map[key] = []
			map[key].push(shift)
		}
		return map
	}, [data.shifts])

	const shiftTypeOptions = useMemo(() => (data.shift_types || []), [data.shift_types])

	const totals = useMemo(() => {
		let plannedShifts = 0
		let workedShifts = 0
		let paidHours = 0
		for (const employee of (data.employees || [])) {
			plannedShifts += employee?.totals?.planned_shifts || 0
			workedShifts += employee?.totals?.worked_shifts || 0
			paidHours += employee?.totals?.paid_hours || 0
		}
		return { plannedShifts, workedShifts, paidHours: Math.round(paidHours * 100) / 100 }
	}, [data.employees])

	function buildFilterParams() {
		return `?start_date=${period.start_date}&end_date=${period.end_date}`
	}

	async function searchWithFilter() {
		const response = await GET(`/work-schedule${buildFilterParams()}`, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			if (!activeShiftTypeId && (response.data.shift_types || []).length) {
				setActiveShiftTypeId(response.data.shift_types[0].id)
			}
		}
	}

	function movePeriod(direction) {
		const start = new Date(period.start_date)
		const end = new Date(period.end_date)
		const length = Math.round((end - start) / (24 * 3600 * 1000)) + 1
		setPeriod({
			start_date: formatDate(addDays(start, direction * length), 'yyyy-MM-dd'),
			end_date: formatDate(addDays(end, direction * length), 'yyyy-MM-dd'),
		})
	}

	function setCurrentWeek() {
		const start = startOfWeek(new Date())
		setPeriod({
			start_date: formatDate(start, 'yyyy-MM-dd'),
			end_date: formatDate(addDays(start, 6), 'yyyy-MM-dd'),
		})
	}

	function setCurrentMonth() {
		const now = new Date()
		setPeriod({
			start_date: formatDate(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'yyyy-MM-dd'),
		})
	}

	/** клик по ячейке ставит выбранную «кистью» смену */
	async function assignShift(employeeId, workDate) {
		if (!activeShiftTypeId) return

		const response = await POST('/work-schedule', {
			work_date: workDate,
			user_id: employeeId,
			work_shift_type_id: activeShiftTypeId,
		}, { loader: true, notification: false })

		if (httpOk(response)) searchWithFilter()
	}

	async function updateShift(payload) {
		const response = await PUT(`/work-schedule/${selectedShift.id}`, payload, { loader: true })
		if (httpOk(response)) {
			setShowCellModal(false)
			searchWithFilter()
		}
	}

	async function markShift(shift, attendanceStatus) {
		const response = await PUT(`/work-schedule/${shift.id}`, {
			attendance_status: attendanceStatus,
		}, { loader: true, notification: false })
		if (httpOk(response)) {
			setShowCellModal(false)
			searchWithFilter()
		}
	}

	async function deleteShift(shiftId) {
		const response = await DELETE(`/work-schedule/${shiftId}`, {}, { loader: true })
		if (httpOk(response)) {
			setShowCellModal(false)
			searchWithFilter()
		}
	}

	/** массовая отметка: весь период или одна строка сотрудника */
	async function markRange(attendanceStatus, employeeId = 0) {
		const payload = {
			attendance_status: attendanceStatus,
			start_date: period.start_date,
			end_date: period.end_date,
		}
		if (employeeId) payload.user_ids = [employeeId]

		const response = await POST('/work-schedule-mark', payload, { loader: true })
		if (httpOk(response)) searchWithFilter()
	}

	/**
	 * Обмен сотрудниками. Если у выбранного сотрудника в этот же день уже стоит смена —
	 * меняем их местами, иначе просто передаём смену ему.
	 */
	async function swapShift() {
		if (!swapUserId) return

		const targetShift = (data.shifts || []).find((shift) =>
			Number(shift.user_id) === Number(swapUserId)
			&& shift.work_date === selectedShift.work_date
		)

		const response = await POST('/work-schedule-swap', targetShift
			? { id: selectedShift.id, target_id: targetShift.id }
			: { id: selectedShift.id, user_id: swapUserId },
			{ loader: true })

		if (httpOk(response)) {
			setShowCellModal(false)
			setSwapUserId(0)
			searchWithFilter()
		}
	}

	async function copySchedule(e) {
		e.preventDefault()

		const response = await POST('/work-schedule-copy', {
			source_start_date: period.start_date,
			source_end_date: period.end_date,
			target_start_date: copyData.target_start_date,
		}, { loader: true })

		if (httpOk(response)) {
			setShowCopyModal(false)
			searchWithFilter()
		}
	}

	async function downloadExcelFile() {
		let urlParams = buildFilterParams()
		urlParams += returnSign(urlParams) + 'excel=true'
		const response = await FILE_D(`/work-schedule${urlParams}`, { loader: true })
		if (httpOk(response)) {
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = fileUrl
			link.setAttribute('download', `work-schedule.xlsx`)
			document.body.appendChild(link)
			link.click()
			URL.revokeObjectURL(fileUrl)
		}
	}

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
		pageStyle: SCHEDULE_PRINT_PAGE_STYLE,
	})

	function openCell(shift) {
		setSelectedShift(shift)
		setSwapUserId(0)
		setShowCellModal(true)
	}

	function shiftStatusClass(shift) {
		if (shift.attendance_status === 'worked') return 'schedule-shift schedule-shift-worked'
		if (shift.attendance_status === 'absent') return 'schedule-shift schedule-shift-absent'
		return 'schedule-shift'
	}

	/** факт по посещаемости есть, а выход не отмечен — повод проверить табель */
	function hasFactMismatch(shift) {
		if (shift.attendance_status === 'planned' && shift.fact_check_in) return true
		if (shift.attendance_status === 'worked' && !shift.fact_check_in) return true
		return false
	}

	useEffect(() => {
		searchWithFilter()
	}, [period]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between d-print-none">
				<h4 className="vertical-center">
					{t('work_schedule')}
				</h4>
				<div className="d-flex gap-2">
					<Link className="btn-rounded btn btn-outline-primary" to="/work-shift-types">
						{t('work_shift_types')}
					</Link>
					<button className="btn-rounded btn btn-outline-primary"
						onClick={() => {
							setCopyData({ target_start_date: formatDate(addDays(new Date(period.end_date), 1), 'yyyy-MM-dd') })
							setShowCopyModal(true)
						}}>
						{t('copy_period')}
					</button>
					<button className="btn-rounded btn btn-outline-primary" onClick={() => handlePrint()}>
						<i className="uil uil-print me-1"></i>
						{t('print')}
					</button>
					<button className="btn-rounded btn btn-outline-primary" onClick={() => downloadExcelFile()}>
						<i className="uil uil-file-download-alt me-1"></i>
						Excel
					</button>
				</div>
			</div>

			<div className="card mb-3 d-print-none">
				<div className="card-body">
					<div className="d-flex flex-wrap gap-3 align-items-end">
						<div className="d-flex gap-1">
							<button className="btn btn-outline-primary" onClick={() => movePeriod(-1)}>
								<i className="uil uil-angle-left"></i>
							</button>
							<button className="btn btn-outline-primary" onClick={() => movePeriod(1)}>
								<i className="uil uil-angle-right"></i>
							</button>
						</div>

						<div className="form-group mb-0">
							<label className="small">{t('period')}</label>
							<PeriodPicker
								startDate={period.start_date}
								endDate={period.end_date}
								onChange={(nextPeriod) => setPeriod({ ...period, ...nextPeriod })}
								withTime={false}
								presets={[]}
							/>
						</div>

						<div className="d-flex gap-1">
							<button className="btn btn-outline-primary" onClick={() => setCurrentWeek()}>{t('week')}</button>
							<button className="btn btn-outline-primary" onClick={() => setCurrentMonth()}>{t('month')}</button>
						</div>

						<div className="form-group mb-0 flex-grow-1">
							<label className="small">{t('active_shift_type')}</label>
							<div className="d-flex flex-wrap gap-2">
								{shiftTypeOptions.map((shiftType) => (
									<button key={shiftType.id}
										className={`btn btn-sm ${Number(activeShiftTypeId) === Number(shiftType.id) ? 'btn-primary' : 'btn-outline-primary'}`}
										onClick={() => setActiveShiftTypeId(shiftType.id)}>
										<span className="shift-color-dot"
											style={{ 'backgroundColor': shiftType.color || '#3b82f6' }}></span>
										{shiftType.name}
										{shiftType.start_time &&
											<span className="ms-1">
												{String(shiftType.start_time).substring(0, 5)}
											</span>
										}
									</button>
								))}
								{!shiftTypeOptions.length &&
									<Link className="btn btn-sm btn-outline-danger" to="/work-shift-types">
										{t('create_shift_type_first')}
									</Link>
								}
							</div>
						</div>

						<div className="d-flex gap-1">
							<button className="btn btn-outline-success"
								onClick={() => markRange('worked')}>
								{t('mark_all_worked')}
							</button>
							<button className="btn btn-outline-secondary"
								onClick={() => markRange('planned')}>
								{t('reset_marks')}
							</button>
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body" ref={printRef}>
					<div className="schedule-print">
						<h5 className="mb-2">
							{t('work_schedule')}: {formatDate(period.start_date)} - {formatDate(period.end_date)}
						</h5>

						<div className="table-responsive">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '180px' }}>{t('employee')}</th>
										{(data.dates || []).map((date) => (
											<th key={date.date}
												className={`text-center ${date.is_weekend ? 'table-secondary' : ''}`}
												style={{ 'minWidth': '110px' }}>
												{date.day}
											</th>
										))}
										<th className="text-center" style={{ 'minWidth': '60px' }}>{t('shifts')}</th>
										<th className="text-center" style={{ 'minWidth': '60px' }}>{t('worked_shifts')}</th>
										<th className="text-center" style={{ 'minWidth': '60px' }}>{t('hours')}</th>
										<th className="text-center d-print-none" style={{ 'minWidth': '40px' }}></th>
									</tr>
								</thead>
								<tbody>
									{(data.employees || []).map((employee) => (
										<tr key={employee.id}>
											<td>
												<div>{employee.first_name}</div>
												<div className="text-muted small">{employee.position || ''}</div>
											</td>
											{(data.dates || []).map((date) => {
												const cellShifts = shiftsByCell[`${employee.id}_${date.date}`] || []
												return (
													<td key={date.date}
														className={`schedule-cell ${date.is_weekend ? 'table-secondary' : ''}`}
														onClick={() => { if (!cellShifts.length) assignShift(employee.id, date.date) }}>
														{cellShifts.map((shift) => (
															<div key={shift.id}
																className={shiftStatusClass(shift)}
																style={{ 'borderColor': shift.color || '#3b82f6' }}
																title={shift.comment || ''}
																onClick={(e) => {
																	e.stopPropagation()
																	openCell(shift)
																}}>
																<span className="shift-color-dot"
																	style={{ 'backgroundColor': shift.color || '#3b82f6' }}></span>
																{shift.shift_name}
																{shift.start_time &&
																	<div className="small">
																		{String(shift.start_time).substring(0, 5)}
																		{shift.end_time ? `-${String(shift.end_time).substring(0, 5)}` : ''}
																	</div>
																}
																{shift.attendance_status === 'worked' &&
																	<i className="uil uil-check text-success"></i>
																}
																{shift.attendance_status === 'absent' &&
																	<i className="uil uil-times text-danger"></i>
																}
																{hasFactMismatch(shift) &&
																	<i className="uil uil-exclamation-triangle text-warning ms-1 d-print-none"
																		title={t('schedule_fact_mismatch')}></i>
																}
															</div>
														))}
													</td>
												)
											})}
											<td className="text-center">{employee?.totals?.planned_shifts || 0}</td>
											<td className="text-center">{employee?.totals?.worked_shifts || 0}</td>
											<td className="text-center">{employee?.totals?.paid_hours || 0}</td>
											<td className="text-center d-print-none">
												<button className="btn btn-sm btn-outline-success"
													title={t('mark_all_worked')}
													onClick={() => markRange('worked', employee.id)}>
													<i className="uil uil-check"></i>
												</button>
											</td>
										</tr>
									))}
									{!(data.employees || []).length &&
										<tr>
											<td colSpan={(data.dates || []).length + 5} className="text-center text-muted">
												{t('list_empty')}
											</td>
										</tr>
									}
								</tbody>
								<tfoot>
									<tr>
										<th>{t('total')}</th>
										<th colSpan={(data.dates || []).length}></th>
										<th className="text-center">{totals.plannedShifts}</th>
										<th className="text-center">{totals.workedShifts}</th>
										<th className="text-center">{totals.paidHours}</th>
										<th className="d-print-none"></th>
									</tr>
								</tfoot>
							</table>
						</div>
					</div>
				</div>
			</div>

			{/* MODAL */}
			<Modal show={showCellModal} animation={false} centered
 onHide={() => setShowCellModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('shift')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{selectedShift &&
						<>
							<div className="mb-3">
								<div><b>{selectedShift.employee_name}</b></div>
								<div>{formatDate(selectedShift.work_date)} — {selectedShift.shift_name}</div>
								<div className="text-muted small">
									{t('paid_hours')}: {selectedShift.paid_hours}
									{selectedShift.payment_mode === 'fixed' &&
										<span className="ms-2">
											{t('fixed_rate')}: {formatMoney(selectedShift.fixed_rate)}
										</span>
									}
									{Number(selectedShift.pay_factor) !== 1 &&
										<span className="ms-2">
											{t('pay_factor')}: {selectedShift.pay_factor}
										</span>
									}
								</div>
								{selectedShift.fact_check_in &&
									<div className="text-muted small">
										{t('attendances')}: {formatDate(selectedShift.fact_check_in, 'dd.MM.yyyy HH:mm')}
										{selectedShift.fact_check_out
											? ` - ${formatDate(selectedShift.fact_check_out, 'dd.MM.yyyy HH:mm')}`
											: ''}
									</div>
								}
							</div>

							<div className="d-flex gap-2 mb-3">
								<button className="btn btn-success w-100"
									onClick={() => markShift(selectedShift, 'worked')}>
									{t('mark_worked')}
								</button>
								<button className="btn btn-outline-danger w-100"
									onClick={() => markShift(selectedShift, 'absent')}>
									{t('mark_absent')}
								</button>
								<button className="btn btn-outline-secondary w-100"
									onClick={() => markShift(selectedShift, 'planned')}>
									{t('reset_marks')}
								</button>
							</div>

							<div className="form-group">
								<label>{t('work_shift_type')}</label>
								<Select
									options={shiftTypeOptions}
									value={shiftTypeOptions.find((x) => Number(x.id) === Number(selectedShift.work_shift_type_id))}
									onChange={(option) => updateShift({ work_shift_type_id: option ? option.id : null })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>

							<div className="form-group">
								<label>{t('swap_employee')}<HintIcon text={t('swap_employee_hint')} /></label>
								<div className="d-flex gap-2">
									<div className="flex-grow-1">
										<Select
											options={(data.employees || []).filter((x) => Number(x.id) !== Number(selectedShift.user_id))}
											value={(data.employees || []).find((x) => Number(x.id) === Number(swapUserId)) || null}
											onChange={(option) => setSwapUserId(option ? option.id : 0)}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.first_name}
											getOptionValue={(option) => option.id}
										/>
									</div>
									<button className="btn btn-outline-primary" disabled={!swapUserId}
										onClick={() => swapShift()}>
										{t('apply')}
									</button>
								</div>
							</div>

							<div className="form-group">
								<label>{t('comment')}</label>
								<textarea className="form-control" rows="2"
									value={selectedShift.comment || ''}
									onChange={(e) => setSelectedShift({ ...selectedShift, 'comment': e.target.value })} />
							</div>

							<div className="d-flex gap-2 w-100 mt-3">
								<button type="button" className="btn btn-outline-danger w-100"
									onClick={() => deleteShift(selectedShift.id)}>
									{t('delete')}
								</button>
								<button type="button" className="btn btn-primary w-100"
									onClick={() => updateShift({ comment: selectedShift.comment || null })}>
									{t('save')}
								</button>
							</div>
						</>
					}
				</Modal.Body>
			</Modal>
			{/* MODAL */}

			{/* MODAL */}
			<Modal show={showCopyModal} animation={false} centered
 onHide={() => setShowCopyModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('copy_period')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={copySchedule} autoComplete="off">
						<div className="mb-3">
							{t('copy_period_hint')}: {formatDate(period.start_date)} - {formatDate(period.end_date)}
						</div>

						<div className="form-group">
							<label>{t('copy_period_target')}<span className="required-mark">*</span></label>
							<div className="calendar-input">
								<DatePicker
									className="form-control"
									dateFormat="dd.MM.yyyy"
									selected={copyData.target_start_date ? new Date(copyData.target_start_date) : ''}
									onChange={(date) => setCopyData({ 'target_start_date': formatDate(date, 'yyyy-MM-dd') })} />
								<i className="uil uil-calendar-alt"></i>
							</div>
						</div>

						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => setShowCopyModal(false)}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100">
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL */}
		</>
	)
}

export default Index
